import React, { useState, useEffect, useCallback, useRef } from 'react';
import T from '../styles/theme';
import { Badge, Btn } from '../components/UI';
import Icon from '../components/Icon';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';
import supabase from '../data/supabase';
import { autoMapColumns } from '../data/importMapper';

/* ═══════════════════════════════════════════════════════
   LINKEDIN HUB — Account Management, KPI Tracking,
   Lead Import & Proxy Settings
   ═══════════════════════════════════════════════════════ */

const cfDate = (d) => { if (!d) return ""; const dt = new Date(d); return (dt.getMonth()+1)+"/"+dt.getDate()+"/"+String(dt.getFullYear()).slice(-2); };

const PROXY_LOCATIONS = [
  { id: "none", label: "No Proxy" },
  { id: "us", label: "United States" },
  { id: "uk", label: "United Kingdom" },
  { id: "mx", label: "Mexico" },
  { id: "ca", label: "Canada" },
  { id: "de", label: "Germany" },
  { id: "au", label: "Australia" },
  { id: "br", label: "Brazil" },
  { id: "in", label: "India" },
];

const KPI_FIELDS = [
  { key: "connections_sent", label: "Connections Sent", targetKey: "daily_limit_connections", color: T.blue },
  { key: "dms_sent", label: "DMs Sent", targetKey: "daily_limit_dms", color: T.pur },
  { key: "comments_posted", label: "Comments", targetKey: "daily_limit_comments", color: T.cyn },
  { key: "followups_sent", label: "Follow-ups", targetKey: "daily_limit_followups", color: T.amb },
  { key: "posts_published", label: "Posts", color: T.grn },
  { key: "inmails_sent", label: "InMails", color: T.red },
  { key: "leads_imported", label: "Leads Imported", color: "#A78BFA" },
  { key: "profile_views", label: "Profile Views", color: T.slateBlue },
];

/* ── Stat Card ── */
function StatCard({ value, label, color, icon }) {
  return (
    <div style={{
      flex: 1, minWidth: 160, padding: "22px 20px", borderRadius: 8,
      background: "#2C2E33",
      border: "1px solid rgba(217,219,221,0.08)",
      transition: T.transition.fast,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon name={icon} size={16} color={color} />
        </div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 300, color: T.txt, letterSpacing: "-0.02em", lineHeight: 1 }}>{typeof value === "number" ? value.toLocaleString() : value}</div>
      <div style={{ fontSize: 11, color: T.tx3, marginTop: 6, letterSpacing: "0.02em" }}>{label}</div>
    </div>
  );
}

/* ── KPI Progress Ring ── */
function KPIRing({ value, target, label, color }) {
  const pct = target > 0 ? Math.min((value / target) * 100, 100) : 0;
  const r = 32;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const hit = pct >= 100;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div style={{ position: "relative", width: 76, height: 76 }}>
        <svg width={76} height={76} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={38} cy={38} r={r} fill="none" stroke={T.bdr} strokeWidth={5} />
          <circle cx={38} cy={38} r={r} fill="none" stroke={hit ? T.grn : color}
            strokeWidth={5} strokeDasharray={circ} strokeDashoffset={offset}
            strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.6s ease" }} />
        </svg>
        <div style={{
          position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
        }}>
          <span style={{ fontSize: 16, fontWeight: 400, color: hit ? T.grn : T.txt }}>{value}</span>
          <span style={{ fontSize: 9, color: T.tx3 }}>/ {target}</span>
        </div>
      </div>
      <span style={{ fontSize: 10, color: T.tx3, textAlign: "center", maxWidth: 80 }}>{label}</span>
    </div>
  );
}

/* ── CSV Parser (RFC 4180-aware, multi-line quoted fields) ── */
// Single-pass character walker maintaining inQ state across newlines.
// Previous implementation split on \n before parsing, fragmenting any
// multi-line quoted cell. Returns the existing shape used by callers
// (Array<{[headerLower]: string}>) so row.name / row.email / etc.
// continue to work in handleImport / preview / dedup paths.
function parseCSV(text) {
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

  const rows = [];
  let row = [];
  let cur = "";
  let inQ = false;
  let i = 0;
  const len = text.length;

  while (i < len) {
    const ch = text[i];

    if (inQ) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cur += '"'; i += 2; continue; }
        inQ = false; i++; continue;
      }
      cur += ch; i++; continue;
    }

    if (ch === '"') { inQ = true; i++; continue; }
    if (ch === ',') { row.push(cur.trim()); cur = ""; i++; continue; }
    if (ch === '\r') {
      row.push(cur.trim()); rows.push(row); row = []; cur = "";
      if (text[i + 1] === '\n') i += 2; else i++;
      continue;
    }
    if (ch === '\n') {
      row.push(cur.trim()); rows.push(row); row = []; cur = "";
      i++; continue;
    }
    cur += ch; i++;
  }

  if (cur.length > 0 || row.length > 0) {
    row.push(cur.trim());
    rows.push(row);
  }

  const nonEmpty = rows.filter(r => r.some(c => c !== ""));
  if (nonEmpty.length < 2) return [];

  // Preserve existing return shape: Array<{ [headerLower: string]: string }>
  const headers = nonEmpty[0].map(h => h.toLowerCase().trim());
  return nonEmpty.slice(1).map(values => {
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = (values[idx] || "").trim(); });
    return obj;
  });
}

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════ */
export default function LinkedInPage({ isMobile }) {
  const auth = useAuth();
  const store = useStore();
  const userId = auth.currentUser?._dbId || auth.currentUser?.id || window.__coreflow_user_id;
  const orgId = auth.currentUser?._orgId || window.__coreflow_org_id;

  // State
  const [tab, setTab] = useState("account");
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [todayKPI, setTodayKPI] = useState(null);
  const [kpiHistory, setKpiHistory] = useState([]);
  const [teamKPIs, setTeamKPIs] = useState([]);
  const [imports, setImports] = useState([]);
  const [showConnect, setShowConnect] = useState(false);
  const [connectForm, setConnectForm] = useState({ profile_url: "", display_name: "", headline: "", email: "", location: "" });
  const [importText, setImportText] = useState("");
  const [importPreview, setImportPreview] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importName, setImportName] = useState("");
  const fileInputRef = useRef(null);

  const tabs = [
    { id: "account", label: "Account" },
    { id: "activity", label: "Daily Activity" },
    { id: "team", label: "Team Board" },
    { id: "import", label: "Lead Import" },
    { id: "settings", label: "Settings" },
  ];

  /* ── Load Data ── */
  const loadAccount = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await supabase.from("linkedin_accounts").select("*").eq("user_id", userId).maybeSingle();
      if (data) setAccount(data);
    } catch (e) { console.warn("[LinkedIn] Load failed:", e.message); }
  }, [userId]);

  const loadTodayKPI = useCallback(async () => {
    if (!userId) return;
    const today = new Date().toISOString().split("T")[0];
    try {
      const { data } = await supabase.from("linkedin_daily_kpis").select("*").eq("user_id", userId).eq("log_date", today).maybeSingle();
      setTodayKPI(data || null);
    } catch (e) { console.warn("[LinkedIn] KPI load failed:", e.message); }
  }, [userId]);

  const loadKpiHistory = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await supabase.from("linkedin_daily_kpis").select("*").eq("user_id", userId).order("log_date", { ascending: false }).limit(30);
      setKpiHistory(data || []);
    } catch (e) { console.warn("[LinkedIn] History load failed:", e.message); }
  }, [userId]);

  const loadTeamKPIs = useCallback(async () => {
    if (!orgId) return;
    const today = new Date().toISOString().split("T")[0];
    try {
      const { data } = await supabase.from("linkedin_daily_kpis").select("*, users!inner(first_name, last_name, avatar_url)").eq("org_id", orgId).eq("log_date", today);
      setTeamKPIs(data || []);
    } catch (e) { console.warn("[LinkedIn] Team KPI load failed:", e.message); }
  }, [orgId]);

  const loadImports = useCallback(async () => {
    if (!orgId) return;
    try {
      const { data } = await supabase.from("linkedin_lead_imports").select("*").eq("org_id", orgId).order("created_at", { ascending: false }).limit(20);
      setImports(data || []);
    } catch (e) { console.warn("[LinkedIn] Imports load failed:", e.message); }
  }, [orgId]);

  useEffect(() => {
    Promise.all([loadAccount(), loadTodayKPI(), loadKpiHistory(), loadTeamKPIs(), loadImports()]).finally(() => setLoading(false));
  }, [loadAccount, loadTodayKPI, loadKpiHistory, loadTeamKPIs, loadImports]);

  /* ── Connect Account ── */
  const handleConnect = async () => {
    if (!connectForm.profile_url && !connectForm.display_name) return;
    setSaving(true);
    try {
      const payload = {
        org_id: orgId, user_id: userId,
        profile_url: connectForm.profile_url,
        display_name: connectForm.display_name || auth.currentUser?.firstName + " " + auth.currentUser?.lastName,
        headline: connectForm.headline,
        email: connectForm.email || auth.currentUser?.email,
        location: connectForm.location,
        status: "connected", connected_at: new Date().toISOString(),
      };
      if (account) {
        await supabase.from("linkedin_accounts").update(payload).eq("id", account.id);
      } else {
        await supabase.from("linkedin_accounts").insert(payload);
      }
      await loadAccount();
      setShowConnect(false);
    } catch (e) { console.warn("[LinkedIn] Connect failed:", e.message); }
    setSaving(false);
  };

  /* ── Disconnect Account ── */
  const handleDisconnect = async () => {
    if (!account || !window.confirm("Disconnect your LinkedIn account? Your activity history will be preserved.")) return;
    await supabase.from("linkedin_accounts").update({ status: "disconnected" }).eq("id", account.id);
    setAccount({ ...account, status: "disconnected" });
  };

  /* ── Update Account Field ── */
  const updateAccountField = async (field, value) => {
    if (!account) return;
    await supabase.from("linkedin_accounts").update({ [field]: value, updated_at: new Date().toISOString() }).eq("id", account.id);
    setAccount(prev => ({ ...prev, [field]: value }));
  };

  /* ── Update Stats ── */
  const updateStats = async (field, value) => {
    if (!account) return;
    const num = parseInt(value) || 0;
    await supabase.from("linkedin_accounts").update({ [field]: num, updated_at: new Date().toISOString() }).eq("id", account.id);
    setAccount(prev => ({ ...prev, [field]: num }));
  };

  /* ── Log KPI ── */
  const logKPI = async (field, value) => {
    const today = new Date().toISOString().split("T")[0];
    const isText = field === "notes";
    const val = isText ? value : (parseInt(value) || 0);
    try {
      if (todayKPI) {
        await supabase.from("linkedin_daily_kpis").update({ [field]: val, updated_at: new Date().toISOString() }).eq("id", todayKPI.id);
        setTodayKPI(prev => ({ ...prev, [field]: val }));
      } else {
        const payload = { org_id: orgId, user_id: userId, log_date: today, [field]: val };
        const { data } = await supabase.from("linkedin_daily_kpis").insert(payload).select().single();
        setTodayKPI(data);
      }
    } catch (e) { console.warn("[LinkedIn] KPI log failed:", e.message); }
  };

  /* ── Lead Import ── */
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImportText(ev.target.result);
      setImportPreview(parseCSV(ev.target.result).slice(0, 10));
      setShowImportModal(true);
    };
    reader.readAsText(file);
  };

  const handlePasteImport = () => {
    if (!importText.trim()) return;
    setImportPreview(parseCSV(importText).slice(0, 10));
    setShowImportModal(true);
  };

  const executeImport = async () => {
    const rows = parseCSV(importText);
    if (!rows.length) return;
    setSaving(true);

    // Run the shared scored auto-mapper once for the whole CSV. parseCSV
    // returns row OBJECTS keyed by lowercased header — so headers come from
    // the first row's keys. autoMapColumns content-sniffs the first 20 rows
    // (e.g. so "Company Size" with values like "11-50" loses to a real
    // "Company" column with "Acme Corp" values for the company field).
    const headers = Object.keys(rows[0] || {});
    const customs = (store?.customFields?.contacts || []).filter(f => f.type !== "calculation");
    const mapping = autoMapColumns(headers, rows.slice(0, 20), customs);
    // mapping is { colIdx: fieldId }; build a fieldId → header reverse lookup.
    const fieldToHeader = {};
    for (const [colIdxStr, fieldId] of Object.entries(mapping)) {
      fieldToHeader[fieldId] = headers[parseInt(colIdxStr, 10)];
    }
    const valueOf = (row, fieldId) => {
      const h = fieldToHeader[fieldId];
      return h ? (row[h] || "") : "";
    };

    // Map all rows to contact objects
    const mapped = rows.map(row => {
      let fn = valueOf(row, "fn");
      let ln = valueOf(row, "ln");
      // Preserve LinkedIn-specific fallback: if the CSV has a single "name"
      // column instead of separate first/last and no fn/ln got mapped, split
      // it on the first space.
      if (!fn && !ln && row["name"]) {
        const parts = (row["name"] || "").split(" ");
        fn = parts[0] || "";
        ln = parts.slice(1).join(" ") || "";
      }
      return {
        fn,
        ln,
        email: valueOf(row, "email"),
        title: valueOf(row, "title"),
        company: valueOf(row, "company"),
        phone: valueOf(row, "phone"),
        linkedinUrl: valueOf(row, "linkedin_url"),
      };
    }).filter(r => r.fn || r.email);

    // Fetch all existing emails in one query to check dupes
    const emails = mapped.map(r => r.email).filter(Boolean);
    let existingEmails = new Set();
    if (emails.length > 0) {
      const { data: existing } = await supabase.from("contacts").select("email").eq("org_id", orgId).in("email", emails);
      if (existing) existingEmails = new Set(existing.map(e => e.email.toLowerCase()));
    }

    // Pre-pass: resolve every company name in the mapped rows to a
    // company_id via ONE batched find-or-create. Per-row lookup below
    // uses the returned map keyed by LOWER(TRIM(name)).
    const companyNames = mapped.map(r => r.company).filter(Boolean);
    const companyMap = await store.findOrCreateCompaniesBulk(companyNames);

    // Split into new vs dupe
    const toInsert = [];
    let dupes = 0;
    for (const r of mapped) {
      if (r.email && existingEmails.has(r.email.toLowerCase())) { dupes++; continue; }
      const normalized = (r.company || "").trim().toLowerCase();
      const companyId = normalized ? companyMap[normalized] || null : null;
      toInsert.push({
        org_id: orgId, fn: r.fn, ln: r.ln, email: r.email, title: r.title,
        co: r.company, company_id: companyId, ph: r.phone,
        source: "LinkedIn", score: 50, status: "new",
        custom_fields: r.linkedinUrl ? { linkedin_url: r.linkedinUrl } : {},
      });
    }

    // Batch insert in chunks of 50
    let imported = 0;
    for (let i = 0; i < toInsert.length; i += 50) {
      const chunk = toInsert.slice(i, i + 50);
      const { data, error } = await supabase.from("contacts").insert(chunk).select("id");
      if (!error && data) imported += data.length;
    }

    // Log import
    await supabase.from("linkedin_lead_imports").insert({
      org_id: orgId, user_id: userId, import_name: importName || "LinkedIn Import " + cfDate(new Date()),
      source: "sales_navigator", total_leads: rows.length,
      imported_count: imported, duplicate_count: dupes, status: "completed",
    });

    // Update today's KPI
    if (todayKPI) {
      await supabase.from("linkedin_daily_kpis").update({ leads_imported: (todayKPI.leads_imported || 0) + imported }).eq("id", todayKPI.id);
    }

    await loadImports();
    await loadTodayKPI();
    if (store.loadContacts) await store.loadContacts();
    setShowImportModal(false);
    setImportText("");
    setImportPreview([]);
    setImportName("");
    setSaving(false);
    alert(`Imported ${imported} contacts. ${dupes} duplicates skipped.`);
  };

  /* ── Styles ── */
  const cardS = {
    padding: 20, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.08)", borderRadius: 8,
    
  };
  const inputS = {
    width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
    background: "rgba(255,255,255,0.04)", color: T.txt, fontSize: 13, fontFamily: "inherit", outline: "none",
    transition: T.transition.fast, boxSizing: "border-box",
  };
  const labelS = { fontSize: 11, color: T.tx3, marginBottom: 6, display: "block", letterSpacing: "0.04em" };
  const sectionLabel = { fontSize: 11, fontWeight: 400, color: T.tx3, letterSpacing: ".08em", marginBottom: 14 };

  const isConnected = account && account.status === "connected";

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: T.tx3 }}>
        Loading LinkedIn Hub...
      </div>
    );
  }

  /* ═══════════════════════════════════════
     RENDER
     ═══════════════════════════════════════ */
  return (
    <div style={{ padding: isMobile ? 16 : 24, maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 300, color: T.txt, margin: 0, letterSpacing: "-0.01em" }}>LinkedIn</h1>
        <p style={{ fontSize: 12, color: T.tx3, margin: "6px 0 0" }}>Manage accounts, track daily activity, and import leads</p>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 2, marginBottom: 24, borderBottom: "1px solid " + T.bdr, paddingBottom: 0 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "10px 18px", fontSize: 12, fontWeight: 400, fontFamily: "inherit",
            color: tab === t.id ? T.txt : T.tx3, background: "none", border: "none",
            borderBottom: tab === t.id ? "2px solid " + T.blue : "2px solid transparent",
            cursor: "pointer", transition: T.transition.fast, marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ══════════════════════════════════════
         TAB: ACCOUNT
         ══════════════════════════════════════ */}
      {tab === "account" && (
        <div>
          {isConnected ? (
            <>
              {/* Profile Card */}
              <div style={{ ...cardS, marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: 14, background: T.blue + "20",
                    border: "2px solid " + T.blue + "40", display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: 22, fontWeight: 300, color: T.blue,
                    overflow: "hidden",
                  }}>
                    {account.avatar_url ? (
                      <img src={account.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      (account.display_name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 20, fontWeight: 300, color: T.txt }}>{account.display_name}</div>
                    {account.headline && <div style={{ fontSize: 12, color: T.tx3, marginTop: 3 }}>{account.headline}</div>}
                    {account.email && <div style={{ fontSize: 11, color: T.tx3, marginTop: 2 }}>{account.email}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <Badge>Connected</Badge>
                    <button onClick={() => { setConnectForm({
                      profile_url: account.profile_url || "",
                      display_name: account.display_name || "",
                      headline: account.headline || "",
                      email: account.email || "",
                      location: account.location || "",
                    }); setShowConnect(true); }}
                      style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx3, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>
                      Edit
                    </button>
                    <button onClick={handleDisconnect}
                      style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.red, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>
                      Disconnect
                    </button>
                  </div>
                </div>

                {/* Account Info Grid */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 40px", padding: "16px 20px", background: "rgba(255,255,255,0.04)", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)" }}>
                  <div>
                    <span style={{ fontSize: 10, color: T.tx3 }}>Profile URL</span>
                    <div style={{ fontSize: 12, color: T.blue, marginTop: 2, wordBreak: "break-all" }}>
                      {account.profile_url ? (
                        <a href={account.profile_url} target="_blank" rel="noreferrer" style={{ color: T.blue, textDecoration: "none" }}>{account.profile_url}</a>
                      ) : "Not set"}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: 10, color: T.tx3 }}>Location</span>
                    <div style={{ fontSize: 12, color: T.txt, marginTop: 2 }}>{account.location || "Not set"}</div>
                  </div>
                  <div>
                    <span style={{ fontSize: 10, color: T.tx3 }}>Account Type</span>
                    <div style={{ fontSize: 12, color: T.txt, marginTop: 2, textTransform: "capitalize" }}>{account.account_type || "Free"}</div>
                  </div>
                  <div>
                    <span style={{ fontSize: 10, color: T.tx3 }}>Connected</span>
                    <div style={{ fontSize: 12, color: T.txt, marginTop: 2 }}>{cfDate(account.connected_at)}</div>
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              <div style={sectionLabel}>ACCOUNT STATS</div>
              <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
                <StatCard value={account.connections || 0} label="Connections" color={T.blue} icon="users" />
                <StatCard value={account.followers || 0} label="Followers" color={T.pur} icon="user" />
                <StatCard value={account.pending_requests || 0} label="Pending Requests" color={T.amb} icon="send" />
              </div>

              {/* Update Stats Inline */}
              <div style={{ ...cardS, marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 14 }}>Update Stats</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  {[
                    { key: "connections", label: "Connections" },
                    { key: "followers", label: "Followers" },
                    { key: "pending_requests", label: "Pending Requests" },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={labelS}>{f.label}</label>
                      <input type="number" value={account[f.key] || 0}
                        onChange={e => updateStats(f.key, e.target.value)}
                        style={inputS} />
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: T.tx3, marginTop: 10 }}>
                  Update these from your LinkedIn profile. Auto-sync available with the Chrome extension.
                </div>
              </div>
            </>
          ) : (
            /* ── Empty State / Connect ── */
            <div style={{ ...cardS, textAlign: "center", padding: 48 }}>
              <div style={{
                width: 72, height: 72, borderRadius: 16, margin: "0 auto 20px",
                background: T.blue + "15", border: "1px solid " + T.blue + "25",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke={T.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6zM2 9h4v12H2zM4 6a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
              </div>
              <h2 style={{ fontSize: 18, fontWeight: 300, color: T.txt, margin: "0 0 8px" }}>Connect Your LinkedIn</h2>
              <p style={{ fontSize: 13, color: T.tx3, maxWidth: 400, margin: "0 auto 24px", lineHeight: 1.6 }}>
                Link your LinkedIn profile to track outreach activity, log daily KPIs, and import leads from Sales Navigator.
              </p>
              <Btn primary onClick={() => setShowConnect(true)}>Connect LinkedIn Account</Btn>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════
         TAB: DAILY ACTIVITY
         ══════════════════════════════════════ */}
      {tab === "activity" && (
        <div>
          {/* Today's KPIs */}
          <div style={sectionLabel}>TODAY'S ACTIVITY — {new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</div>

          {/* Ring Summary */}
          {account && (
            <div style={{ ...cardS, marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "center", gap: isMobile ? 14 : 28, flexWrap: "wrap", padding: "10px 0" }}>
                {KPI_FIELDS.filter(f => f.targetKey).map(f => (
                  <KPIRing key={f.key} value={todayKPI?.[f.key] || 0}
                    target={account?.[f.targetKey] || 35} label={f.label} color={f.color} />
                ))}
              </div>
            </div>
          )}

          {/* Log Activity */}
          <div style={{ ...cardS, marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 16 }}>Log Activity</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 12 }}>
              {KPI_FIELDS.map(f => (
                <div key={f.key}>
                  <label style={labelS}>{f.label}</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input type="number" min={0} value={todayKPI?.[f.key] || 0}
                      onChange={e => logKPI(f.key, e.target.value)}
                      style={{ ...inputS, flex: 1 }} />
                    <button onClick={() => logKPI(f.key, (todayKPI?.[f.key] || 0) + 1)}
                      style={{
                        width: 36, height: 36, borderRadius: 8, border: "1px solid " + f.color + "40",
                        background: f.color + "15", color: f.color, fontSize: 16,
                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: "inherit", transition: T.transition.fast,
                      }}>+</button>
                  </div>
                </div>
              ))}
            </div>
            {/* Notes */}
            <div style={{ marginTop: 16 }}>
              <label style={labelS}>Notes</label>
              <textarea value={todayKPI?.notes || ""} onChange={e => logKPI("notes", e.target.value)}
                placeholder="Any notes for today's outreach..."
                style={{ ...inputS, height: 60, resize: "vertical" }} />
            </div>
          </div>

          {/* History */}
          <div style={sectionLabel}>ACTIVITY HISTORY</div>
          <div style={{ ...cardS }}>
            {kpiHistory.length === 0 ? (
              <div style={{ textAlign: "center", padding: 32, color: T.tx3, fontSize: 13 }}>No activity logged yet. Start tracking above.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "1px solid " + T.bdr, color: T.tx3, fontWeight: 400 }}>Date</th>
                      {KPI_FIELDS.slice(0, 6).map(f => (
                        <th key={f.key} style={{ textAlign: "center", padding: "8px 6px", borderBottom: "1px solid " + T.bdr, color: T.tx3, fontWeight: 400 }}>{f.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {kpiHistory.map(row => (
                      <tr key={row.id} style={{ borderBottom: "1px solid " + T.bdr }}>
                        <td style={{ padding: "10px 10px", color: T.txt }}>{cfDate(row.log_date)}</td>
                        {KPI_FIELDS.slice(0, 6).map(f => {
                          const v = row[f.key] || 0;
                          const target = account?.[f.targetKey];
                          const hit = target && v >= target;
                          return (
                            <td key={f.key} style={{ textAlign: "center", padding: "10px 6px", color: hit ? T.grn : T.txt, fontWeight: hit ? 500 : 400 }}>{v}</td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
         TAB: TEAM BOARD
         ══════════════════════════════════════ */}
      {tab === "team" && (
        <div>
          <div style={sectionLabel}>TEAM LINKEDIN ACTIVITY — TODAY</div>
          {teamKPIs.length === 0 ? (
            <div style={{ ...cardS, textAlign: "center", padding: 40, color: T.tx3, fontSize: 13 }}>
              No team activity logged for today yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {teamKPIs.map(tk => {
                const name = tk.users ? (tk.users.first_name + " " + tk.users.last_name) : "Team Member";
                const totalActions = (tk.connections_sent || 0) + (tk.dms_sent || 0) + (tk.comments_posted || 0) + (tk.followups_sent || 0);
                return (
                  <div key={tk.id} style={cardS}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: 12, background: T.blue + "18",
                        border: "1px solid " + T.blue + "25", display: "flex", alignItems: "center",
                        justifyContent: "center", fontSize: 14, fontWeight: 300, color: T.blue,
                      }}>
                        {name.split(" ").map(w => w[0]).join("").slice(0, 2)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>{name}</div>
                        <div style={{ fontSize: 11, color: T.tx3 }}>{totalActions} actions today</div>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                      {KPI_FIELDS.filter(f => f.targetKey).map(f => (
                        <div key={f.key} style={{ textAlign: "center", padding: "10px 0", borderRadius: 8, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.08)" }}>
                          <div style={{ fontSize: 18, fontWeight: 300, color: f.color }}>{tk[f.key] || 0}</div>
                          <div style={{ fontSize: 9, color: T.tx3, marginTop: 4 }}>{f.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════
         TAB: LEAD IMPORT
         ══════════════════════════════════════ */}
      {tab === "import" && (
        <div>
          <div style={sectionLabel}>IMPORT LEADS FROM LINKEDIN</div>
          <div style={{ ...cardS, marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, marginBottom: 6 }}>Import from CSV or paste data</div>
            <p style={{ fontSize: 12, color: T.tx3, marginBottom: 16, lineHeight: 1.5 }}>
              Export your leads from Sales Navigator or any LinkedIn tool as CSV. We'll match columns automatically and skip duplicates.
            </p>

            <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
              <input ref={fileInputRef} type="file" accept=".csv,.tsv,.txt" onChange={handleFileUpload} style={{ display: "none" }} />
              <Btn primary onClick={() => fileInputRef.current?.click()}>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><Icon name="upload" size={14} /> Upload CSV</span>
              </Btn>
              <button onClick={() => setShowImportModal(true)}
                style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.txt, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                Paste Data
              </button>
            </div>

            {/* Column mapping info */}
            <div style={{ padding: 16, background: "rgba(255,255,255,0.04)", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)" }}>
              <div style={{ fontSize: 11, fontWeight: 400, color: T.tx3, marginBottom: 8 }}>SUPPORTED COLUMNS</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {["First Name", "Last Name", "Email", "Title", "Company", "Phone", "LinkedIn URL"].map(col => (
                  <span key={col} style={{
                    padding: "4px 10px", borderRadius: 6, background: T.blue + "12",
                    border: "1px solid " + T.blue + "20", color: T.txt, fontSize: 10,
                  }}>{col}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Import History */}
          <div style={sectionLabel}>IMPORT HISTORY</div>
          <div style={cardS}>
            {imports.length === 0 ? (
              <div style={{ textAlign: "center", padding: 32, color: T.tx3, fontSize: 13 }}>No imports yet.</div>
            ) : (
              <div>
                {imports.map(imp => (
                  <div key={imp.id} style={{
                    display: "flex", alignItems: "center", gap: 14, padding: "14px 0",
                    borderBottom: "1px solid " + T.bdr,
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8, background: T.grn + "15",
                      border: "1px solid " + T.grn + "25", display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Icon name="download" size={14} color={T.grn} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: T.txt }}>{imp.import_name || "LinkedIn Import"}</div>
                      <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>
                        {imp.imported_count} imported, {imp.duplicate_count} duplicates  —  {cfDate(imp.created_at)}
                      </div>
                    </div>
                    <Badge>{imp.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
         TAB: SETTINGS (Proxy + Limits)
         ══════════════════════════════════════ */}
      {tab === "settings" && (
        <div>
          {!isConnected ? (
            <div style={{ ...cardS, textAlign: "center", padding: 40, color: T.tx3, fontSize: 13 }}>
              Connect your LinkedIn account first to configure settings.
            </div>
          ) : (
            <>
              {/* Proxy Settings */}
              <div style={sectionLabel}>PROXY SETTINGS</div>
              <div style={{ ...cardS, marginBottom: 20 }}>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={labelS}>Proxy Location</label>
                    <select value={account.proxy_location || "none"}
                      onChange={e => updateAccountField("proxy_location", e.target.value)}
                      style={{ ...inputS, cursor: "pointer" }}>
                      {PROXY_LOCATIONS.map(p => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </select>
                    <div style={{ fontSize: 10, color: T.tx3, marginTop: 6 }}>
                      Route LinkedIn traffic through a proxy to match your profile location and avoid restrictions.
                    </div>
                  </div>
                  <div>
                    <label style={labelS}>Account Type</label>
                    <select value={account.account_type || "free"}
                      onChange={e => updateAccountField("account_type", e.target.value)}
                      style={{ ...inputS, cursor: "pointer" }}>
                      <option value="free">Free</option>
                      <option value="premium">Premium</option>
                      <option value="sales_navigator">Sales Navigator</option>
                      <option value="recruiter">Recruiter</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 20, marginTop: 20 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: T.txt }}>
                    <input type="checkbox" checked={account.proxy_enabled || false}
                      onChange={e => updateAccountField("proxy_enabled", e.target.checked)}
                      style={{ accentColor: T.blue }} />
                    Enable Proxy
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: T.txt }}>
                    <input type="checkbox" checked={account.warmup_enabled || false}
                      onChange={e => updateAccountField("warmup_enabled", e.target.checked)}
                      style={{ accentColor: T.blue }} />
                    Account Warmup
                  </label>
                </div>
              </div>

              {/* Daily Limits */}
              <div style={sectionLabel}>DAILY LIMITS</div>
              <div style={{ ...cardS, marginBottom: 20 }}>
                <p style={{ fontSize: 12, color: T.tx3, marginBottom: 16, lineHeight: 1.5 }}>
                  Set daily outreach targets. These are used as goals in the Activity tracker and as safety limits for automation.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 14 }}>
                  {[
                    { key: "daily_limit_connections", label: "Connections / day", default: 35 },
                    { key: "daily_limit_dms", label: "DMs / day", default: 25 },
                    { key: "daily_limit_comments", label: "Comments / day", default: 30 },
                    { key: "daily_limit_followups", label: "Follow-ups / day", default: 40 },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={labelS}>{f.label}</label>
                      <input type="number" min={0} value={account[f.key] ?? f.default}
                        onChange={e => updateAccountField(f.key, parseInt(e.target.value) || 0)}
                        style={inputS} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Danger Zone */}
              <div style={sectionLabel}>ACCOUNT</div>
              <div style={{ ...cardS }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: 13, color: T.txt }}>Disconnect LinkedIn</div>
                    <div style={{ fontSize: 11, color: T.tx3, marginTop: 2 }}>Your activity history and settings will be preserved.</div>
                  </div>
                  <button onClick={handleDisconnect}
                    style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid " + T.red + "40", background: T.red + "12", color: T.red, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                    Disconnect
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════
         MODAL: Connect LinkedIn
         ══════════════════════════════════════ */}
      {showConnect && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
        }} onClick={(e) => { if (e.target === e.currentTarget) setShowConnect(false); }}>
          <div style={{
            width: 480, maxWidth: "90vw", background: T.bg, border: "1px solid rgba(217,219,221,0.08)",
            borderRadius: 14, padding: 28, boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <h2 style={{ fontSize: 16, fontWeight: 400, color: T.txt, margin: 0 }}>
                {account ? "Edit LinkedIn Profile" : "Connect LinkedIn"}
              </h2>
              <button onClick={() => setShowConnect(false)} style={{ background: "none", border: "none", color: T.tx3, cursor: "pointer", fontSize: 18 }}>
                <Icon name="x" size={18} />
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={labelS}>LinkedIn Profile URL</label>
                <input value={connectForm.profile_url} onChange={e => setConnectForm(p => ({ ...p, profile_url: e.target.value }))}
                  placeholder="https://www.linkedin.com/in/yourprofile" style={inputS} />
              </div>
              <div>
                <label style={labelS}>Display Name</label>
                <input value={connectForm.display_name} onChange={e => setConnectForm(p => ({ ...p, display_name: e.target.value }))}
                  placeholder="Hunter Hopkins" style={inputS} />
              </div>
              <div>
                <label style={labelS}>Headline / Job Title</label>
                <input value={connectForm.headline} onChange={e => setConnectForm(p => ({ ...p, headline: e.target.value }))}
                  placeholder="Co-Founder & CTO @CoreFlow" style={inputS} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelS}>Email</label>
                  <input value={connectForm.email} onChange={e => setConnectForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="hunter@core-flow.io" style={inputS} />
                </div>
                <div>
                  <label style={labelS}>Location</label>
                  <input value={connectForm.location} onChange={e => setConnectForm(p => ({ ...p, location: e.target.value }))}
                    placeholder="Mexico City" style={inputS} />
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
              <button onClick={() => setShowConnect(false)}
                style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx3, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <Btn primary onClick={handleConnect} disabled={saving}>
                {saving ? "Saving..." : (account ? "Save Changes" : "Connect Account")}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
         MODAL: Import Preview
         ══════════════════════════════════════ */}
      {showImportModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
        }} onClick={(e) => { if (e.target === e.currentTarget) setShowImportModal(false); }}>
          <div style={{
            width: 680, maxWidth: "90vw", maxHeight: "80vh", background: T.bg,
            border: "1px solid rgba(217,219,221,0.08)", borderRadius: 14, padding: 28,
            boxShadow: "0 24px 80px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <h2 style={{ fontSize: 16, fontWeight: 400, color: T.txt, margin: 0 }}>Import LinkedIn Leads</h2>
              <button onClick={() => setShowImportModal(false)} style={{ background: "none", border: "none", color: T.tx3, cursor: "pointer" }}>
                <Icon name="x" size={18} />
              </button>
            </div>

            {importPreview.length === 0 ? (
              /* Paste mode */
              <div style={{ flex: 1 }}>
                <label style={labelS}>Paste CSV data below</label>
                <textarea value={importText} onChange={e => setImportText(e.target.value)}
                  placeholder={"First Name,Last Name,Email,Title,Company,Phone,LinkedIn URL\nTessa,Rigby,tessa@agency.com,Creative Director,Rigby Design,,https://linkedin.com/in/tessarigby"}
                  style={{ ...inputS, height: 200, resize: "vertical", fontFamily: "monospace", fontSize: 11 }} />
                <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
                  <button onClick={() => setShowImportModal(false)}
                    style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx3, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                    Cancel
                  </button>
                  <Btn primary onClick={handlePasteImport}>Preview Import</Btn>
                </div>
              </div>
            ) : (
              /* Preview mode */
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelS}>Import Name</label>
                  <input value={importName} onChange={e => setImportName(e.target.value)}
                    placeholder="Sales Navigator — Marketing Agencies Batch 1" style={inputS} />
                </div>
                <div style={{ fontSize: 12, color: T.tx3, marginBottom: 10 }}>
                  Preview (showing first {importPreview.length} of {parseCSV(importText).length} rows):
                </div>
                <div style={{ flex: 1, overflow: "auto", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <thead>
                      <tr>
                        {Object.keys(importPreview[0] || {}).map(h => (
                          <th key={h} style={{ textAlign: "left", padding: "8px 10px", borderBottom: "1px solid " + T.bdr, color: T.tx3, fontWeight: 400, whiteSpace: "nowrap", textTransform: "capitalize" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.map((row, i) => (
                        <tr key={i}>
                          {Object.values(row).map((v, j) => (
                            <td key={j} style={{ padding: "8px 10px", borderBottom: "1px solid " + T.bdr, color: T.txt, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 11, color: T.tx3 }}>{parseCSV(importText).length} leads will be processed. Duplicates auto-skipped by email.</div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={() => { setImportPreview([]); }}
                      style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx3, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                      Back
                    </button>
                    <Btn primary onClick={executeImport} disabled={saving}>
                      {saving ? "Importing..." : "Import " + parseCSV(importText).length + " Leads"}
                    </Btn>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
